import { CxAgentStatusMap } from '../stores/CxFloorStore';
import { useMyFloorStore } from '@/apps/office/stores/myFloorStore';

/** The minimum shape needed to judge whether an occupant locks a room. */
export interface RoomOccupant {
  pid: string;
  name?: string;
  status?: string;
}

/**
 * Find the occupant who is locking a room, if any.
 *
 * A room is locked for exactly one reason: an occupant's NexGen presence status is
 * 'locked'. Being on a call is deliberately NOT checked here.
 *
 * The two are separate on purpose. A call starting locks the room via `applyCallLock`,
 * which sets that presence status like any other lock — but the agent can then unlock it
 * and let colleagues in while the call continues. The red ring keeps showing they are on
 * a call; the room simply isn't shut any more. Deriving the lock from the dialer as well
 * would override that choice and make the unlock button a lie.
 *
 * @param usersInRoom - Occupants of the room being judged
 * @param agentStatuses - Live dialer statuses, keyed by user pid (not a lock source)
 * @returns The locking occupant, or undefined when the room is open
 */
export const findRoomLockOwner = <T extends RoomOccupant>(
  usersInRoom: T[],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  agentStatuses: CxAgentStatusMap
): T | undefined => usersInRoom.find((user) => user.status === 'locked');

/**
 * Whether a room is locked at all.
 * @param usersInRoom - Occupants of the room being judged
 * @param agentStatuses - Live dialer statuses, keyed by user pid
 */
export const isRoomLockedByOccupants = <T extends RoomOccupant>(
  usersInRoom: T[],
  agentStatuses: CxAgentStatusMap
): boolean => Boolean(findRoomLockOwner(usersInRoom, agentStatuses));

/**
 * Apply the current user's own dialer status to their room lock.
 *
 * Called wherever statuses arrive — the floor-load fetch and the live socket update — so the
 * lock follows a call starting and ending without a dedicated effect or component.
 *
 * @param agentStatuses - Live dialer statuses, keyed by user pid
 * @param ownUserPid - The signed-in user's pid
 */
export const syncOwnCallLock = (agentStatuses: CxAgentStatusMap, ownUserPid?: string): void => {
  if (!ownUserPid) return;
  useMyFloorStore.getState().applyCallLock(agentStatuses[ownUserPid]?.status === 'on_call');
};
